import React, { useEffect, useMemo, useState } from 'react';
import {
  FiArrowLeft,
  FiChevronRight,
  FiFolderPlus,
  FiMoreHorizontal,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import ShareAccessModal from '../common/share_access_modal';
import TodoInput from './input_todo';
import TodoItemDetail from './todo_item_detail';
import TodoMovePicker from './todo_move_picker';
import { formatDueLabel, isOverdue } from './parse_deadline';
import { addTodo, deleteTodo, fetchTodos, updateTodo } from './todo_api';
import './todo.css';

function childrenOf(items, parentId) {
  const key = parentId ?? null;
  return items.filter((item) => (item.parent_id ?? null) === key);
}

function descendantIds(items, rootId) {
  const ids = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const nextParent = queue.shift();
    for (const item of items) {
      if (item.parent_id === nextParent && !ids.has(item.id)) {
        ids.add(item.id);
        queue.push(item.id);
      }
    }
  }
  return ids;
}

function sortNodes(nodes) {
  return [...nodes].sort((a, b) => {
    if (!!a.completed !== !!b.completed) return a.completed ? 1 : -1;
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    return (a.position || 0) - (b.position || 0) || a.id - b.id;
  });
}

function TodoRow({ item, childCount, onOpen, onToggle, onMenu }) {
  const dueLabel = item.due_at ? formatDueLabel(item.due_at) : null;
  return (
    <div className={`todo-row${item.completed ? ' is-done' : ''}${item.urgent ? ' is-urgent' : ''}`}>
      <input type="checkbox" className="todo-check" checked={!!item.completed} onChange={onToggle} aria-label={`Complete ${item.title}`} />
      <button type="button" className="todo-row-main" onClick={onOpen}>
        <span className="todo-row-title-line">
          <span className="todo-row-title">{item.title}</span>
          {item.pinned && <span className="todo-pin" title="Pinned">Pinned</span>}
        </span>
        <span className="todo-row-meta">
          {childCount > 0 && <span>{childCount} item{childCount === 1 ? '' : 's'}</span>}
          {dueLabel && <span className={isOverdue(item) ? 'is-overdue' : ''}>{dueLabel}</span>}
          {item.venue_name && <span>{item.venue_name}</span>}
          {item.is_shared && item.owner_name && <span className="todo-shared-by">Shared by {item.owner_name}</span>}
          {(item.shared_with_all || (item.shared_user_ids || []).length > 0) && <span className="todo-shared-by"><FiUsers /> Shared</span>}
        </span>
      </button>
      {childCount > 0 && <FiChevronRight className="todo-row-chevron" />}
      <button type="button" className="todo-icon-btn" onClick={onMenu} aria-label={`More options for ${item.title}`}><FiMoreHorizontal /></button>
    </div>
  );
}

export default function Todo() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [parentId, setParentId] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [movingItem, setMovingItem] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [addingFolder, setAddingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');

  const load = async () => {
    try {
      const data = await fetchTodos();
      setItems(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load todos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentParent = items.find((item) => item.id === parentId) || null;
  const parents = useMemo(() => {
    const result = [];
    const byId = new Map(items.map((item) => [item.id, item]));
    let cursor = currentParent;
    const seen = new Set();
    while (cursor && !seen.has(cursor.id)) {
      result.unshift(cursor);
      seen.add(cursor.id);
      cursor = byId.get(cursor.parent_id);
    }
    return result;
  }, [items, currentParent]);

  const visibleItems = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    const rows = trimmed
      ? items.filter((item) => `${item.title} ${item.description || ''} ${item.venue_name || ''}`.toLowerCase().includes(trimmed))
      : childrenOf(items, parentId);
    return sortNodes(rows);
  }, [items, parentId, query]);

  const pinned = useMemo(() => sortNodes(items.filter((item) => item.pinned && !item.completed)).slice(0, 4), [items]);
  const pending = visibleItems.filter((item) => !item.completed);
  const completed = visibleItems.filter((item) => item.completed);
  const childCount = (id) => childrenOf(items, id).length;

  const patchLocal = (updated) => {
    setItems((current) => current.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
    setDetailItem((current) => current?.id === updated.id ? { ...current, ...updated } : current);
  };

  const handleAdd = async (payload) => {
    const created = await addTodo({ ...payload, parent_id: parentId });
    setItems((current) => [...current, created]);
  };

  const handleAddFolder = async () => {
    const title = folderName.trim();
    if (!title) return;
    const created = await addTodo({ title, kind: 'folder', parent_id: parentId });
    setItems((current) => [...current, created]);
    setFolderName('');
    setAddingFolder(false);
  };

  const handleChange = async (patch) => {
    const updated = await updateTodo(patch);
    patchLocal(updated);
    return updated;
  };

  const handleDelete = async (item) => {
    const count = descendantIds(items, item.id).size - 1;
    const isAdmin = localStorage.getItem('walmates-login_id') === 'walmates';
    const action = isAdmin ? 'Permanently delete' : 'Remove from your todos';
    if (!window.confirm(`${action} “${item.title}”${count ? ` and ${count} item${count === 1 ? '' : 's'} inside` : ''}?`)) return;
    const result = await deleteTodo(item.id);
    const removed = new Set(result.deleted_ids || [item.id]);
    setItems((current) => current.filter((row) => !removed.has(row.id)));
    if (removed.has(parentId)) setParentId(null);
    setDetailItem(null);
    setMenuItem(null);
  };

  const handleMove = async (nextParentId) => {
    if (!movingItem) return;
    const updated = await updateTodo({ id: movingItem.id, parent_id: nextParentId });
    patchLocal(updated);
    setMovingItem(null);
  };

  const openItem = (item) => {
    if (item.kind === 'folder' || childCount(item.id) > 0) {
      setParentId(item.id);
      setQuery('');
    } else {
      setDetailItem(item);
    }
  };

  const goBack = () => {
    setParentId(currentParent?.parent_id ?? null);
    setQuery('');
  };

  return (
    <div className="todo-shell">
      <header className="todo-header">
        <div className="todo-header-brand">
          {currentParent && <button type="button" className="todo-back" onClick={goBack} aria-label="Back"><FiArrowLeft /></button>}
          <div><div className="todo-header-kicker">Todo</div><h1>{currentParent?.title || 'All lists'}</h1></div>
        </div>
        <div className="todo-header-actions">
          {currentParent?.can_manage_sharing && <button type="button" className="todo-btn todo-btn-ghost" onClick={() => setShareItem(currentParent)}><FiUsers /> Share list</button>}
          <button type="button" className="todo-btn todo-btn-primary" onClick={() => setAddingFolder(true)}><FiFolderPlus /> New list</button>
        </div>
      </header>

      {parents.length > 1 && <nav className="todo-breadcrumbs" aria-label="Breadcrumb"><button type="button" onClick={() => setParentId(null)}>All lists</button>{parents.slice(0, -1).map((parent) => <button key={parent.id} type="button" onClick={() => setParentId(parent.id)}>{parent.title}</button>)}</nav>}

      <div className="todo-workspace">
        <aside className="todo-sidebar">
          <div className="todo-search"><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search or add…" />{query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search"><FiX /></button>}</div>
          <div className="todo-composer"><TodoInput onAdd={handleAdd} placeholder={currentParent ? `Add in ${currentParent.title}` : 'Add an item, e.g. milk today 6 pm'} /></div>
          {parentId == null && pinned.length > 0 && <section className="todo-pinned-card"><div className="todo-pinned-heading">Pinned</div>{pinned.map((item) => <button type="button" key={item.id} onClick={() => openItem(item)}><span>{item.title}</span>{item.due_at && <small>{formatDueLabel(item.due_at)}</small>}</button>)}</section>}
          <div className="todo-summary"><strong>{pending.length}</strong> open <span>·</span> <strong>{completed.length}</strong> completed</div>
        </aside>

        <main className="todo-main">
          {loading && <div className="todo-empty">Loading…</div>}
          {error && <div className="todo-empty todo-error">{error}</div>}
          {!loading && !error && visibleItems.length === 0 && <div className="todo-empty-panel"><h2>{query ? 'No matches' : 'Nothing here yet'}</h2><p>{query ? 'Try a different search.' : 'Add an item or create a list to get started.'}</p></div>}
          {!loading && !error && pending.length > 0 && <section className="todo-section"><h3>{query ? 'Results' : 'Open'}</h3>{pending.map((item) => <TodoRow key={item.id} item={item} childCount={childCount(item.id)} onOpen={() => openItem(item)} onToggle={() => handleChange({ id: item.id, completed: true })} onMenu={() => setMenuItem(item)} />)}</section>}
          {!loading && !error && completed.length > 0 && <section className="todo-section todo-section-done"><h3>Completed</h3>{completed.map((item) => <TodoRow key={item.id} item={item} childCount={childCount(item.id)} onOpen={() => openItem(item)} onToggle={() => handleChange({ id: item.id, completed: false })} onMenu={() => setMenuItem(item)} />)}</section>}
        </main>
      </div>

      {addingFolder && <div className="todo-modal-backdrop" role="presentation" onClick={() => setAddingFolder(false)}><div className="todo-modal todo-create-list" role="dialog" onClick={(event) => event.stopPropagation()}><div className="todo-modal-header"><h2>New list</h2><button type="button" className="todo-icon-btn" onClick={() => setAddingFolder(false)}><FiX /></button></div><label className="todo-field"><span>List name</span><input autoFocus value={folderName} onChange={(event) => setFolderName(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && handleAddFolder()} placeholder="Costco" /></label><div className="todo-modal-actions todo-modal-actions-end"><button type="button" className="todo-btn todo-btn-ghost" onClick={() => setAddingFolder(false)}>Cancel</button><button type="button" className="todo-btn todo-btn-primary" onClick={handleAddFolder} disabled={!folderName.trim()}>Create list</button></div></div></div>}

      {detailItem && <TodoItemDetail item={items.find((item) => item.id === detailItem.id) || detailItem} onClose={() => setDetailItem(null)} onChange={handleChange} onPatch={patchLocal} onDelete={handleDelete} />}
      {movingItem && <TodoMovePicker items={items} movingItem={movingItem} onClose={() => setMovingItem(null)} onMove={handleMove} />}
      {shareItem && <ShareAccessModal resource="todo" itemId={shareItem.id} title={shareItem.title} onClose={() => setShareItem(null)} onSaved={(settings) => patchLocal({ ...shareItem, shared_with_all: settings.shared_with_all, shared_user_ids: settings.user_ids })} />}

      {menuItem && <div className="todo-modal-backdrop" role="presentation" onClick={() => setMenuItem(null)}><div className="todo-sheet" role="menu" onClick={(event) => event.stopPropagation()}><button type="button" onClick={() => { setDetailItem(menuItem); setMenuItem(null); }}>Edit details</button>{(menuItem.kind === 'folder' || childCount(menuItem.id) > 0) && <button type="button" onClick={() => { openItem(menuItem); setMenuItem(null); }}>Open list</button>}{menuItem.can_manage_sharing && (menuItem.kind === 'folder' || childCount(menuItem.id) > 0) && <button type="button" onClick={() => { setShareItem(menuItem); setMenuItem(null); }}><FiUsers /> Share list</button>}{(menuItem.parent_id != null || menuItem.can_manage_sharing) && <button type="button" onClick={() => { setMovingItem(menuItem); setMenuItem(null); }}>Move</button>}<button type="button" onClick={async () => { await handleChange({ id: menuItem.id, pinned: !menuItem.pinned }); setMenuItem(null); }}>{menuItem.pinned ? 'Unpin' : 'Pin'}</button>{menuItem.kind !== 'folder' && <button type="button" onClick={async () => { await handleChange({ id: menuItem.id, urgent: !menuItem.urgent }); setMenuItem(null); }}>{menuItem.urgent ? 'Clear urgent' : 'Mark urgent'}</button>}<button type="button" className="is-danger" onClick={() => handleDelete(menuItem)}>Delete</button></div></div>}
    </div>
  );
}
